import { useTaskViewMode } from "../useTaskViewMode";
import { useTasks } from "@hq/client-react";
import { coarseQueryFor } from "../TaskList";
import type { TaskFilters, TaskGroupBy, TaskSort, TaskView } from "@hq/contracts";
import { defineView } from "@hq/core";
import { useState } from "react";
import { type TagCount, useAllTags } from "../tags";

export type ViewState = {
  view: TaskView;
  mode: "list" | "board";
  setMode: (mode: "list" | "board") => void;
  hasTasks: boolean;
  sort: TaskSort;
  setSort: (sort: TaskSort) => void;
  groupBy: TaskGroupBy;
  setGroupBy: (groupBy: TaskGroupBy) => void;
  showCompleted: boolean;
  setShowCompleted: (show: boolean) => void;
  /** Active tag filter (any of these, case-insensitive). Empty means no tag filter. */
  tags: readonly string[];
  setTags: (tags: readonly string[]) => void;
  toggleTag: (tag: string) => void;
  /** Every tag in use across tasks, for the filter menu. */
  allTags: TagCount[];
};

function withTags(filters: TaskFilters, tags: readonly string[]): TaskFilters {
  if (tags.length === 0) return filters;
  return { ...filters, tags: [...tags] };
}

/** Widens a status filter to include completed tasks when the toggle is on. */
function withCompleted(filters: TaskFilters, showCompleted: boolean): TaskFilters {
  if (!showCompleted) return filters;
  const base = filters.status ?? ["open", "someday"];
  return { ...filters, status: base.includes("completed") ? base : [...base, "completed"] };
}

/**
 * Local display overrides on top of a built-in view. Seeded once from the base view; the
 * derived `view` is rebuilt on every render through `defineView`, never stored.
 */
export function useViewState(base: TaskView): ViewState {
  const [mode, setMode] = useTaskViewMode(base.id);
  const [sort, setSort] = useState<TaskSort>(base.sort);
  const [groupBy, setGroupBy] = useState<TaskGroupBy>(base.groupBy);
  const [showCompleted, setShowCompleted] = useState(base.display.showCompleted);
  const [tags, setTags] = useState<readonly string[]>(base.filters.tags ?? []);
  const allTags = useAllTags();
  const toggleTag = (tag: string) => {
    const key = tag.toLowerCase();
    const has = tags.some((t) => t.toLowerCase() === key);
    setTags(has ? tags.filter((t) => t.toLowerCase() !== key) : [...tags, tag]);
  };
  const view = defineView({
    ...base,
    sort,
    groupBy,
    filters: withTags(withCompleted(base.filters, showCompleted), tags),
    display: { ...base.display, showCompleted },
  });
  const { data } = useTasks(coarseQueryFor(view));
  const hasTasks = (data?.length ?? 0) > 0;
  return {
    view,
    mode,
    setMode,
    hasTasks,
    sort,
    setSort,
    groupBy,
    setGroupBy,
    showCompleted,
    setShowCompleted,
    tags,
    setTags,
    toggleTag,
    allTags,
  };
}
